import React from "react";
import { Box, Grid, Typography, useTheme, Paper } from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import { AllocationType } from "../../types";
import AllocationsBarItem from "./AllocationsBarItem";
import { colors } from "../../constants";
import AllocationsBarLegendsItem from "./AllocationsBarLegendsItem";

interface AllocationsBarProps {
  data: AllocationsListItemCellType[];
  isFuture?: boolean;
  current?: number;
  min: number;
  max: number;
  startIndex?: number;
  setCurrent: (current: number) => void;
}

export interface AllocationsListItemCellType {
  start: number;
  end: number;
  title: string;
  label?: React.ReactNode;
  data?: AllocationType;
}

const AllocationsBar: React.FC<AllocationsBarProps> = ({ data, isFuture, current = 0, startIndex = 0, min, max, setCurrent }) => {
  const theme = useTheme();
  const useLeftStyles = makeStyles(() => ({
    root: {
      display: "flex",
      alignItems: "center",
      flexBasis: 80,
      flexShrink: 0,
      flexGrow: 0,
      backgroundColor: "#f6f6f6",
      borderRight: "1px solid #cccccc",
      padding: theme.spacing(2),
    },
  }));
  const leftStyles = useLeftStyles();
  const total = max - min;
  const bgColor = !isFuture ? "#ffffff" : "#f6f6f6";

  let legends = Array.from(
    new Set(
      data
        .filter(cell => (cell.data?.operators?.length ?? 0) > 0)
        //@ts-ignore
        .map(cell => (cell.data?.operators?.length > 1 ? "Multiple" : cell.title)),
    ),
  );
  let assignedWithoutOperator = false;

  return (
    <>
      <Box bgcolor={bgColor} border="1px solid #cccccc" width="100%">
        <Grid container alignItems="stretch" wrap="nowrap">
          <Paper classes={leftStyles}>
            <Typography>{isFuture ? "Future" : "Current"}</Typography>
          </Paper>
          <Grid size="grow">
            <Box width="100%" position="relative" flexGrow={1} flexShrink={1} height={56} overflow="hidden">
              {data.map((cell, index) => {
                const { start, end, title } = cell;
                const colorFindIndex = legends.findIndex(legend => legend === title);

                let assigned = false;
                let operatorDefined = false;
                if (cell.data) {
                  assigned = cell.data.assigned === "Y";
                }
                if (cell.data?.operators) {
                  operatorDefined = cell.data.operators.length > 0;
                }

                let color = !!cell.data
                  ? cell.data.assigned !== "Y"
                    ? "#ffffff"
                    : colors[colorFindIndex > -1 ? colorFindIndex : legends.length + startIndex + index]
                  : bgColor;

                if (!operatorDefined && assigned) {
                  assignedWithoutOperator = true;
                  color = "#808080";
                }

                const left = ((start - min) / total) * 100;
                const width = ((end - start) / total) * 100;
                const text = !!cell.data
                  ? `${cell.data.band_start} MHz - ${cell.data.band_end} MHz `
                  : `${cell.start} MHz - ${cell.end} MHz No Data`;

                return (
                  <AllocationsBarItem
                    key={index}
                    title={text}
                    width={width}
                    left={left}
                    color={(cell.data?.operators?.length ?? 0) > 1 ? "#000000" : color}
                    isFuture={isFuture}
                    active={current === startIndex + index}
                    onClick={() => {
                      setCurrent(startIndex + index);
                    }}
                  />
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box overflow="auto" width="100%">
        {/* Legends */}
        <Box paddingY={4} paddingX={2}>
          <Grid container wrap="nowrap" spacing={4}>
            {legends.map((legend, index) => {
              return (
                <AllocationsBarLegendsItem
                  key={index + 2}
                  multiple={legend.indexOf("Multiple") > -1}
                  color={colors[index]}
                  value={legend}
                />
              );
            })}
            {assignedWithoutOperator && <AllocationsBarLegendsItem key={1} color="#808080" value="Assigned" />}
            <AllocationsBarLegendsItem key={0} color={!isFuture ? "#ffffff" : "#ffffff"} value="Unassigned / No data" />
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default AllocationsBar;
